// SPDX-License-Identifier: LGPL-2.1-or-later
import { V3, type Vec2, type Vec3 } from "./vec.ts";

/**
 * A placed plane with a local 2D frame (used for sketches). `xAxis` and `normal` are unit length and
 * orthogonal; the local Y axis is `normal × xAxis` (right-handed). Create with {@link plane} to normalise.
 */
export interface Plane3 {
  readonly origin: Vec3;
  readonly xAxis: Vec3;
  readonly normal: Vec3;
}

/** An infinite axis: a point and a unit direction. */
export interface Axis3 {
  readonly origin: Vec3;
  readonly direction: Vec3;
}

export function plane(origin: Vec3, xAxis: Vec3, normal: Vec3): Plane3 {
  const n = V3.normalize(normal);
  let x = V3.normalize(V3.sub(xAxis, V3.scale(n, V3.dot(xAxis, n))));
  if (V3.length(x) < 0.5) x = V3.anyPerpendicular(n);
  return { origin, xAxis: x, normal: n };
}

export const axis = (origin: Vec3, direction: Vec3): Axis3 => ({ origin, direction: V3.normalize(direction) });

export const Planes = {
  /** World XY plane (top), normal +Z. */
  XY: plane(V3.zero, V3.unitX, V3.unitZ),
  /** World XZ plane (front), normal −Y so local Y maps to world +Z. */
  XZ: plane(V3.zero, V3.unitX, { x: 0, y: -1, z: 0 }),
  /** World YZ plane (right), normal +X: local X = +Y, local Y = +Z. */
  YZ: plane(V3.zero, V3.unitY, V3.unitX),

  yAxis: (p: Plane3): Vec3 => V3.cross(p.normal, p.xAxis),

  toWorld: (p: Plane3, q: Vec2): Vec3 => {
    const y = V3.cross(p.normal, p.xAxis);
    return {
      x: p.origin.x + p.xAxis.x * q.x + y.x * q.y,
      y: p.origin.y + p.xAxis.y * q.x + y.y * q.y,
      z: p.origin.z + p.xAxis.z * q.x + y.z * q.y,
    };
  },

  directionToWorld: (p: Plane3, d: Vec2): Vec3 =>
    V3.add(V3.scale(p.xAxis, d.x), V3.scale(V3.cross(p.normal, p.xAxis), d.y)),

  /** Projects a world point into plane coordinates. */
  toLocal: (p: Plane3, q: Vec3): Vec2 => {
    const d = V3.sub(q, p.origin);
    return { x: V3.dot(d, p.xAxis), y: V3.dot(d, V3.cross(p.normal, p.xAxis)) };
  },

  signedDistance: (p: Plane3, q: Vec3): number => V3.dot(V3.sub(q, p.origin), p.normal),

  offset: (p: Plane3, distance: number): Plane3 => ({ ...p, origin: V3.add(p.origin, V3.scale(p.normal, distance)) }),

  /** Ray/plane intersection, or null when parallel. */
  intersectRay: (p: Plane3, origin: Vec3, direction: Vec3): Vec3 | null => {
    const denom = V3.dot(direction, p.normal);
    if (Math.abs(denom) < 1e-12) return null;
    const t = V3.dot(V3.sub(p.origin, origin), p.normal) / denom;
    return V3.add(origin, V3.scale(direction, t));
  },
};

export const Axes = {
  X: axis(V3.zero, V3.unitX),
  Y: axis(V3.zero, V3.unitY),
  Z: axis(V3.zero, V3.unitZ),
};
